import { Card, Button, Spinner } from '@agentisai/design-system';
import { Navigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { safeNextPath } from '../router/safeNextPath';
import * as styles from './LoginPage.css';

/**
 * LoginPage — public. The on-brand login landing (S-E4): a centred brand
 * lockup over a 420px auth card, matching the agentis2 prototype's
 * `#screen-login`. Reads a validated ?next= return path and seeds it into
 * the Auth0 login (appState.returnTo) so post-callback lands back there.
 * Already-authenticated visitors are redirected home.
 *
 * Auth0 hosted login is the real auth — there is no demo-account picker,
 * password field, or "Forgot password?" here; the single "Log in" CTA hands
 * off to Auth0.
 */
export function LoginPage() {
  const { isLoading, isAuthenticated, error, login } = useAuth();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));

  if (isLoading) {
    return (
      <main className={styles.page}>
        <Spinner size="lg" tone="primary" label="Loading…" />
      </main>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.lockup}>
        <span className={styles.brand}>Agentis Ai</span>
        <span className={styles.product}>GENESIS</span>
      </div>
      <Card elevation={1} padding="lg" className={styles.card}>
        {error ? (
          <>
            <h1 className={styles.title}>Sign-in error</h1>
            <p className={styles.body}>{error.message}</p>
            <Button variant="primary" size="lg" fullWidth onClick={() => login(next)}>
              Try again
            </Button>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Welcome to Agentis AI Genesis</h1>
            <p className={styles.subtitle}>Sign in to continue</p>
            <p className={styles.body}>
              Authentication is handled by Auth0. Sign in to access your workstreams.
            </p>
            <Button variant="primary" size="lg" fullWidth onClick={() => login(next)}>
              Log in
            </Button>
          </>
        )}
      </Card>
    </main>
  );
}
LoginPage.displayName = 'LoginPage';
